import React from 'react';

const GAP_MAP = {
  4: 'var(--space-xs)',
  8:  'var(--space-s)',
  12:  'var(--space-m)',
  16:  'var(--space-l)',
  24: 'var(--space-xl)',
  32: 'var(--space-2xl)',
  48: 'var(--space-3xl)',
  64: 'var(--space-4xl)',
  128: 'var(--space-6xl)',
  256: 'var(--space-8xl)',
};

/**
 * TabGroup — Container for TabItem components.
 *
 * Handles layout (flex row, gap) and visual group style.
 * Injects `variant` into each direct TabItem child so callers
 * don't need to repeat it on every item.
 *
 * @param {string}  variant      - 'block' | 'line' | 'block-mono' (default: 'block')
 * @param {string}  gap          - spacing token key: 'xs'|'s'|'m'|'l'|'xl' (default: 'm')
 * @param {boolean} fullWidth    - stretch to fill container width, distributing gap equally between tabs
 * @param {string}  customClass  - additional class names
 * @param {object}  style        - inline style overrides
 */
export const TabGroup = ({
  children,
  variant = 'block',
  gap = '12',
  fullWidth = false,
  customClass = '',
  style = {},
}) => {
  const classes = ['tab-group', `tab-group--${variant}`, customClass]
    .filter(Boolean)
    .join(' ');

  const gapValue = GAP_MAP[gap] ?? gap;

  const injectedChildren = React.Children.map(children, (child) =>
    React.isValidElement(child) ? React.cloneElement(child, { variant }) : child
  );

  const layoutStyle = fullWidth
    ? { width: '100%', justifyContent: 'space-between', ...style }
    : { gap: gapValue, ...style };

  return (
    <div className={classes} style={layoutStyle}>
      {injectedChildren}
    </div>
  );
};
